import { type ReactNode, useMemo } from "react";
import { Area, AreaChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import { formatValueCurrency } from "@/components/widgets/helpers/FormattingHelpers";
import { componentClassNames } from "@/components/widgets/Quote/config";
import { getOppositeValue } from "../../../lib/helpers/getOppositeValue";
import { CompanyTemplate, type FinancialWithPeriod } from "../../../types";

const defaultChartConfig = {
	ebitDa: { label: "EBITDA", color: "var(--gold)" },
	totalRevenue: { label: "Sales", color: "var(--success-light)" },
	totalOperatingExpenses: {
		label: "OpEx",
		color: "var(--destructive)",
	},
	operatingCostOfRevenue: {
		label: "COGS",
		color: "var(--red)",
	},
} satisfies ChartConfig;

const finChartConfig = {
	totalInterestIncome: {
		label: "Total Interest Income",
		color: "var(--success)",
	},
	totalCostOfRevenue: {
		label: "Total Cost of Revenue",
		color: "var(--error-light)",
	},
	netIncomeToCommon: {
		label: "Net Income",
		color: "var(--success-light)",
	},
} satisfies ChartConfig;

export const IncomeStatementChart = ({
	data,
	header,
	template,
}: {
	data: FinancialWithPeriod[];
	header?: ReactNode;
	template?: CompanyTemplate;
}) => {
	const isFinTemplate = template === CompanyTemplate.Fin;
	const chartConfig = isFinTemplate ? finChartConfig : defaultChartConfig;

	const chartData = useMemo(() => {
		if (isFinTemplate) {
			return data
				.filter(
					(item) =>
						item?.income_statement?.totalinterestincome != null ||
						item?.income_statement?.totalcostofrevenue != null ||
						item?.income_statement?.netincometocommon != null,
				)
				.map((item) => ({
					totalInterestIncome: item?.income_statement?.totalinterestincome,
					totalCostOfRevenue: getOppositeValue(
						item?.income_statement?.totalcostofrevenue,
					),
					netIncomeToCommon: item?.income_statement?.netincometocommon,
					period: item?.period,
				}));
		}
		return data
			.filter(
				(item) =>
					item?.calculations?.ebitda != null ||
					item?.income_statement?.totalrevenue != null ||
					item?.income_statement?.operatingcostofrevenue != null ||
					item?.income_statement?.totaloperatingexpenses != null,
			)
			.map((item) => ({
				ebitDa: item?.calculations?.ebitda,
				totalRevenue: item?.income_statement?.totalrevenue,
				operatingCostOfRevenue: getOppositeValue(
					item?.income_statement?.operatingcostofrevenue,
				),
				totalOperatingExpenses: getOppositeValue(
					item?.income_statement?.totaloperatingexpenses,
				),
				period: item.period,
			}));
	}, [data, isFinTemplate]);

	const renderGradients = () => {
		if (isFinTemplate) {
			return (
				<defs>
					<linearGradient
						id="fillTotalInterestIncome"
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop
							offset="5%"
							stopColor="var(--color-totalInterestIncome)"
							stopOpacity={0.8}
						/>
						<stop
							offset="95%"
							stopColor="var(--color-totalInterestIncome)"
							stopOpacity={0.1}
						/>
					</linearGradient>
					<linearGradient
						id="fillTotalCostOfRevenue"
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop
							offset="5%"
							stopColor="var(--color-totalCostOfRevenue)"
							stopOpacity={0.8}
						/>
						<stop
							offset="95%"
							stopColor="var(--color-totalCostOfRevenue)"
							stopOpacity={0.1}
						/>
					</linearGradient>
					<linearGradient
						id="fillNetIncomeToCommon"
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop
							offset="5%"
							stopColor="var(--color-netIncomeToCommon)"
							stopOpacity={0.8}
						/>
						<stop
							offset="95%"
							stopColor="var(--color-netIncomeToCommon)"
							stopOpacity={0.1}
						/>
					</linearGradient>
				</defs>
			);
		}

		return (
			<defs>
				<linearGradient id="fillEbitDa" x1="0" y1="0" x2="0" y2="1">
					<stop offset="5%" stopColor="var(--color-ebitDa)" stopOpacity={0.8} />
					<stop
						offset="95%"
						stopColor="var(--color-ebitDa)"
						stopOpacity={0.1}
					/>
				</linearGradient>
				<linearGradient id="fillTotalRevenue" x1="0" y1="0" x2="0" y2="1">
					<stop
						offset="5%"
						stopColor="var(--color-totalRevenue)"
						stopOpacity={0.8}
					/>
					<stop
						offset="95%"
						stopColor="var(--color-totalRevenue)"
						stopOpacity={0.1}
					/>
				</linearGradient>
				<linearGradient
					id="fillTotalOperatingExpenses"
					x1="0"
					y1="0"
					x2="0"
					y2="1"
				>
					<stop
						offset="5%"
						stopColor="var(--color-totalOperatingExpenses)"
						stopOpacity={0.8}
					/>
					<stop
						offset="95%"
						stopColor="var(--color-totalOperatingExpenses)"
						stopOpacity={0.1}
					/>
				</linearGradient>
				<linearGradient
					id="fillOperatingCostOfRevenue"
					x1="0"
					y1="0"
					x2="0"
					y2="1"
				>
					<stop
						offset="5%"
						stopColor="var(--color-operatingCostOfRevenue)"
						stopOpacity={0.8}
					/>
					<stop
						offset="95%"
						stopColor="var(--color-operatingCostOfRevenue)"
						stopOpacity={0.1}
					/>
				</linearGradient>
			</defs>
		);
	};

	const finTemplateAreas = [
		<Area
			key="totalInterestIncome"
			dataKey="totalInterestIncome"
			type="natural"
			fill="url(#fillTotalInterestIncome)"
			fillOpacity={0.4}
			stroke="var(--color-totalInterestIncome)"
			stackId="positive"
		/>,
		<Area
			key="totalCostOfRevenue"
			dataKey="totalCostOfRevenue"
			type="natural"
			fill="url(#fillTotalCostOfRevenue)"
			fillOpacity={0.4}
			stroke="var(--color-totalCostOfRevenue)"
			stackId="negative"
		/>,
		<Area
			key="netIncomeToCommon"
			dataKey="netIncomeToCommon"
			type="natural"
			fill="url(#fillNetIncomeToCommon)"
			fillOpacity={0.4}
			stroke="var(--color-netIncomeToCommon)"
			stackId="positive"
		/>,
	];

	const defaultTemplateAreas = [
		<Area
			key="ebitDa"
			dataKey="ebitDa"
			type="natural"
			fill="url(#fillEbitDa)"
			fillOpacity={0.4}
			stroke="var(--color-ebitDa)"
			stackId="positive"
		/>,
		<Area
			key="totalRevenue"
			dataKey="totalRevenue"
			type="natural"
			fill="url(#fillTotalRevenue)"
			fillOpacity={0.4}
			stroke="var(--color-totalRevenue)"
			stackId="positive"
		/>,
		<Area
			key="totalOperatingExpenses"
			dataKey="totalOperatingExpenses"
			type="natural"
			fill="url(#fillTotalOperatingExpenses)"
			fillOpacity={0.4}
			stroke="var(--color-totalOperatingExpenses)"
			stackId="negative"
		/>,
		<Area
			key="operatingCostOfRevenue"
			dataKey="operatingCostOfRevenue"
			type="natural"
			fill="url(#fillOperatingCostOfRevenue)"
			fillOpacity={0.4}
			stroke="var(--color-operatingCostOfRevenue)"
			stackId="negative"
		/>,
	];

	const templateAreas = isFinTemplate ? finTemplateAreas : defaultTemplateAreas;

	if (!chartData.length) return null;

	return (
		<Card className={componentClassNames} data-testid="income-statement-chart">
			<CardHeader className="p-0">
				<CardTitle className="text-center">
					{header || "Income Statement"}
				</CardTitle>
			</CardHeader>
			<CardContent className="p-0">
				<ChartContainer config={chartConfig} className="w-full">
					<AreaChart accessibilityLayer data={chartData}>
						<XAxis
							dataKey="period"
							tickLine={false}
							axisLine={false}
							tickMargin={0}
						/>
						<YAxis
							tickLine={false}
							axisLine={false}
							tickMargin={0}
							tickFormatter={(value) => formatValueCurrency(value)}
							width={50}
						/>
						<ChartTooltip
							cursor={false}
							content={(props) => (
								<ChartTooltipContent
									{...props}
									indicator="line"
									valueFormatter={formatValueCurrency}
									hideIndicator
								/>
							)}
						/>

						{renderGradients()}

						{templateAreas}

						<ChartLegend content={<ChartLegendContent />} />
					</AreaChart>
				</ChartContainer>
			</CardContent>
		</Card>
	);
};
